import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import GuineaMap from "../components/GuineaMap";
import { toast } from "sonner";
import {
  Building2,
  Briefcase,
  FileText,
  CheckCircle,
  Clock,
  XCircle,
  ArrowRight,
  TrendingUp,
  Bell,
  Mail,
  CalendarClock,
  Loader2,
  Send,
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardIGT() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [recentBilans, setRecentBilans] = useState([]);
  const [reminderStatus, setReminderStatus] = useState(null);
  const [sendingReminders, setSendingReminders] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, bilansRes, reminderRes] = await Promise.all([
          axios.get(`${API_URL}/statistics/national`),
          axios.get(`${API_URL}/bilans?status=soumis`),
          axios.get(`${API_URL}/admin/reminders/status`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: null }))
        ]);
        setStats(statsRes.data);
        setRecentBilans(bilansRes.data.slice(0, 5));
        if (reminderRes.data) {
          setReminderStatus(reminderRes.data);
        }
      } catch (error) {
        console.error(/* PROD */ "Failed to fetch dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const handleTriggerReminders = async () => {
    setSendingReminders(true);
    try {
      await axios.post(`${API_URL}/admin/reminders/trigger`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Vérification des rappels effectuée");
      // Refresh status
      const res = await axios.get(`${API_URL}/admin/reminders/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReminderStatus(res.data);
    } catch (error) {
      toast.error("Erreur lors de l'envoi des rappels");
    } finally {
      setSendingReminders(false);
    }
  };

  const handleRegionClick = (regionName) => {
    setSelectedRegion(regionName === selectedRegion ? null : regionName);
  };

  const getSelectedRegionStats = () => {
    if (!selectedRegion || !stats) return null;
    return stats.regions.find((r) => r.region === selectedRegion);
  };

  const StatCard = ({ title, value, icon: Icon, color, href }) => (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-heading font-bold mt-1">{value}</p>
          </div>
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${color}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
        {href && (
          <Link
            to={href}
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline mt-4"
          >
            Voir détails <ArrowRight className="w-4 h-4" />
          </Link>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-[400px] w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  const selectedStats = getSelectedRegionStats();

  return (
    <div className="space-y-6" data-testid="dashboard-igt">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">
          Tableau de Bord National
        </h1>
        <p className="text-muted-foreground mt-1">
          Vue d'ensemble des bilans sociaux en République de Guinée
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Cabinets Actifs"
          value={stats?.cabinets_actifs || 0}
          icon={Building2}
          color="bg-primary"
          href="/cabinets"
        />
        <StatCard
          title="Entreprises"
          value={stats?.total_entreprises || 0}
          icon={Briefcase}
          color="bg-slate-700"
          href="/entreprises"
        />
        <StatCard
          title="Bilans Validés"
          value={stats?.bilans_valides || 0}
          icon={CheckCircle}
          color="bg-emerald-600"
          href="/bilans?status=valide"
        />
        <StatCard
          title="En Attente"
          value={stats?.bilans_en_attente || 0}
          icon={Clock}
          color="bg-amber-500"
          href="/bilans?status=soumis"
        />
      </div>

      {/* Map and Region Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-heading">Carte de Conformité</CardTitle>
            <CardDescription>
              Cliquez sur une région pour voir les détails
            </CardDescription>
          </CardHeader>
          <CardContent>
            <GuineaMap
              regionsStats={stats?.regions || []}
              onRegionClick={handleRegionClick}
              selectedRegion={selectedRegion}
            />
          </CardContent>
        </Card>

        {/* Region Details or Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">
              {selectedRegion ? `Région: ${selectedRegion}` : "Résumé National"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedRegion && selectedStats ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-sm text-slate-600">Entreprises</span>
                  <span className="font-semibold">{selectedStats.total_entreprises}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                  <span className="text-sm text-emerald-700">Bilans Validés</span>
                  <span className="font-semibold text-emerald-700">
                    {selectedStats.bilans_valides}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                  <span className="text-sm text-amber-700">Bilans Soumis</span>
                  <span className="font-semibold text-amber-700">
                    {selectedStats.bilans_soumis}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <span className="text-sm text-red-700">Bilans Rejetés</span>
                  <span className="font-semibold text-red-700">
                    {selectedStats.bilans_rejetes}
                  </span>
                </div>
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600">Statut de conformité:</span>
                    <span
                      className={`badge-${
                        selectedStats.conformite === "vert"
                          ? "success"
                          : selectedStats.conformite === "jaune"
                          ? "warning"
                          : "error"
                      }`}
                    >
                      {selectedStats.conformite === "vert"
                        ? "Conforme"
                        : selectedStats.conformite === "jaune"
                        ? "Partiel"
                        : "Non conforme"}
                    </span>
                  </div>
                </div>
                <Link to={`/entreprises?region=${selectedRegion}`}>
                  <Button className="w-full mt-4" variant="outline">
                    Voir les entreprises
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-primary/5 rounded-lg">
                  <TrendingUp className="w-8 h-8 text-primary" />
                  <div>
                    <p className="font-semibold text-slate-900">
                      {stats?.total_bilans || 0} Bilans
                    </p>
                    <p className="text-sm text-slate-600">Total déclarations</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-emerald-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-emerald-700">
                      {stats?.bilans_valides || 0}
                    </p>
                    <p className="text-xs text-emerald-600">Validés</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-amber-700">
                      {stats?.bilans_en_attente || 0}
                    </p>
                    <p className="text-xs text-amber-600">En attente</p>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-red-700">
                      {stats?.bilans_rejetes || 0}
                    </p>
                    <p className="text-xs text-red-600">Rejetés</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-slate-700">8</p>
                    <p className="text-xs text-slate-600">Régions</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Reminder System Card */}
      {reminderStatus && (
        <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                <Bell className="w-5 h-5 text-white" />
              </div>
              <div>
                <CardTitle className="font-heading text-lg">Système de Rappels Automatiques</CardTitle>
                <CardDescription>
                  Envoi automatique d'emails aux cabinets avant la date limite
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                reminderStatus.scheduler_running 
                  ? "bg-green-100 text-green-700" 
                  : "bg-red-100 text-red-700"
              }`}>
                <span className={`w-2 h-2 rounded-full ${
                  reminderStatus.scheduler_running ? "bg-green-500 animate-pulse" : "bg-red-500"
                }`}></span>
                {reminderStatus.scheduler_running ? "Actif" : "Inactif"}
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Days Left */}
              <div className="p-4 bg-white rounded-lg border">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <CalendarClock className="w-4 h-4" />
                  <span className="text-xs font-medium">Jours restants</span>
                </div>
                <p className={`text-2xl font-bold ${
                  reminderStatus.days_left <= 7 ? "text-red-600" :
                  reminderStatus.days_left <= 30 ? "text-amber-600" : "text-blue-600"
                }`}>
                  {reminderStatus.days_left}
                </p>
                <p className="text-xs text-slate-500">
                  Date limite: 31 mars {new Date().getFullYear()}
                </p>
              </div>

              {/* Pending Cabinets */}
              <div className="p-4 bg-white rounded-lg border">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <Building2 className="w-4 h-4" />
                  <span className="text-xs font-medium">Cabinets en retard</span>
                </div>
                <p className="text-2xl font-bold text-amber-600">
                  {reminderStatus.cabinets_with_pending_declarations}
                </p>
                <p className="text-xs text-slate-500">
                  {reminderStatus.total_pending_enterprises} entreprises à déclarer
                </p>
              </div>

              {/* Reminders Sent */}
              <div className="p-4 bg-white rounded-lg border">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <Mail className="w-4 h-4" />
                  <span className="text-xs font-medium">Rappels envoyés</span>
                </div>
                <p className="text-2xl font-bold text-green-600">
                  {reminderStatus.total_reminders_sent}
                </p>
                <p className="text-xs text-slate-500">
                  Pour l'exercice {reminderStatus.bilan_year}
                </p>
              </div>

              {/* Manual Trigger */}
              <div className="p-4 bg-white rounded-lg border flex flex-col justify-between">
                <div className="flex items-center gap-2 text-slate-600 mb-2">
                  <Send className="w-4 h-4" />
                  <span className="text-xs font-medium">Déclencher manuellement</span>
                </div>
                <Button 
                  size="sm" 
                  onClick={handleTriggerReminders}
                  disabled={sendingReminders}
                  className="w-full"
                  data-testid="trigger-reminders-btn"
                >
                  {sendingReminders ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Envoi...
                    </>
                  ) : (
                    <>
                      <Bell className="w-4 h-4 mr-2" />
                      Vérifier maintenant
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Reminder Schedule Info */}
            <div className="mt-4 p-3 bg-blue-100/50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Calendrier des rappels :</strong> Les emails sont envoyés automatiquement à 
                <span className="font-semibold"> 30, 15, 7, 3 et 1 jour(s)</span> avant la date limite 
                aux cabinets ayant des entreprises sans bilan déclaré.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Submissions */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-heading">Bilans en Attente de Validation</CardTitle>
            <CardDescription>Dernières soumissions à examiner</CardDescription>
          </div>
          <Link to="/bilans?status=soumis">
            <Button variant="outline" size="sm">
              Voir tout
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {recentBilans.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucun bilan en attente de validation</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentBilans.map((bilan) => (
                <Link
                  key={bilan.id}
                  to={`/bilans/${bilan.id}`}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-amber-700" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        {bilan.enterprise_name || "Entreprise"}
                      </p>
                      <p className="text-sm text-slate-500">
                        Année {bilan.annee} • {bilan.region || "N/A"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="badge-warning">En attente</span>
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
